import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { SocietyAiPlanTier } from 'types/Society';
import { setModal } from 'modules/_shared/redux/modalActions';
import cleanText from 'utils/cleanText';

import {
  appendAiMessage,
  getAiUsage,
  setAiHandoff,
} from 'modules/ai/redux/aiActions';
import { useAiChatStream } from 'modules/ai/hooks/useAiChatStream';
import { useNavigateToAiAgent } from 'modules/ai/hooks/useNavigateToAiAgent';
import AiQuotaExceededModal from 'modules/ai/modals/AiQuotaExceededModal';
import { AiQuotaExceededModalProvider } from 'modules/ai/modals/AiQuotaExceededModal/context/AiQuotaExceededModalContext';
import { createMessageId } from 'modules/ai/pages/SocietyAiAgent/utils/createMessageId';

type UseEmbedAiComposerParams = {
  societyId: string;
};

export const useEmbedAiComposer = ({ societyId }: UseEmbedAiComposerParams) => {
  const dispatch = useDispatch();
  const navigateToAiAgent = useNavigateToAiAgent();

  const [inputText, setInputText] = useState('');
  const conversationIdRef = useRef<string | null>(null);

  const isStreaming = useSelector((state: State) => state.ai?.isStreaming ?? false);
  const usage = useSelector((state: State) => state.ai?.usage);
  const activeConversationId = useSelector(
    (state: State) => state.ai?.currentConversation?.id ?? null
  );

  const { sendMessage, error } = useAiChatStream();

  useEffect(() => {
    if (activeConversationId) {
      conversationIdRef.current = activeConversationId;
    }
  }, [activeConversationId]);

  useEffect(() => {
    if (error === 'AI_QUOTA_EXCEEDED' && usage) {
      dispatch(
        setModal(
          <AiQuotaExceededModalProvider
            used={usage.used || 0}
            limit={usage.limit || 0}
            tier={usage.tier || SocietyAiPlanTier.FREE}
          >
            <AiQuotaExceededModal />
          </AiQuotaExceededModalProvider>
        )
      );
    }
  }, [dispatch, error, usage]);

  const selectSuggestion = useCallback((text: string) => {
    setInputText(cleanText(text));
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmed = inputText.trim();
    if (!trimmed || !societyId || isStreaming) return;

    dispatch(
      appendAiMessage({
        id: createMessageId('u'),
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      })
    );
    dispatch(setAiHandoff(true));
    setInputText('');

    try {
      await sendMessage({
        societyId,
        conversationId: conversationIdRef.current,
        message: trimmed,
        persistOnUnmount: true,
        onConversationId: (id) => {
          conversationIdRef.current = id;
        },
        onFirstChunk: () => {
          navigateToAiAgent({ societyId, handoff: true });
        },
        onDone: () => {
          dispatch(getAiUsage(societyId) as any);
        },
      });
    } catch {
      // quota exceeded handled via error effect above
    }
  }, [
    dispatch,
    inputText,
    isStreaming,
    navigateToAiAgent,
    sendMessage,
    societyId,
  ]);

  return {
    inputText,
    isStreaming,
    selectSuggestion,
    handleSubmit,
    setInputText,
  };
};
